import { useLayoutEffect, useState } from 'react';
import type { DataTableMode } from '@odo/shared-ui';
import { useViewportPageSize } from './useViewportPageSize';

interface ViewportTable {
    mode: DataTableMode;
    limit: number;
    setLimit: (limit: number) => void;
}

interface ViewportTableLimitOptions {
    rowHeight: number;
    /** Replaces the viewport fit, e.g. for an embedded pane with no refs attached. */
    fixedPageSize?: number;
    /** Space below the measured element; defaults to the card's pb-8 wrapper. */
    bottomSpace?: number;
}

// Fits a useDataTable's paged limit to the viewport. Gate the paged fetch on isReady:
// both syncs run as layout effects, so no unready frame paints and the fetch asks once.
export function useViewportTableLimit(
    table: ViewportTable,
    { rowHeight, fixedPageSize, bottomSpace }: ViewportTableLimitOptions
) {
    const viewport = useViewportPageSize(undefined, rowHeight, bottomSpace);
    const { remeasure } = viewport;
    const pageSize = fixedPageSize ?? viewport.pageSize;
    const { mode, limit, setLimit } = table;

    // Paged and infinite footers differ in height, so each mode is measured before the gate opens.
    const [measuredMode, setMeasuredMode] = useState<DataTableMode | null>(null);
    useLayoutEffect(() => {
        remeasure();
        setMeasuredMode(mode);
    }, [mode, remeasure]);

    // A no-op in infinite mode, where useDataTable locks limit to its infiniteLimit.
    useLayoutEffect(() => {
        if (limit !== pageSize) setLimit(pageSize);
    }, [limit, pageSize, setLimit]);

    const isMeasured = fixedPageSize !== undefined || measuredMode === mode;
    return {
        tableCardRef: viewport.tableCardRef,
        tableHeaderRef: viewport.tableHeaderRef,
        tableBodyRef: viewport.tableBodyRef,
        paginationRef: viewport.paginationRef,
        remeasure,
        isReady: isMeasured && limit === pageSize
    };
}
